import React, { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

// One row of the Cancel pop-up reason list: tick · ✎ edit (name, description + the whole customer e-mail) · ✕ remove.
export function CancelReasonRow({ reason: r, checked, onToggle }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ label: r.label || "", description: r.description || "", email_subject: "", email_greeting: "", email_body: "" });
  const refresh = () => qc.invalidateQueries({ queryKey: ["cancel-reasons"] });
  const { data: mail, isLoading: mailLoading } = useQuery({
    queryKey: ["cancel-reason-email", r.id],
    queryFn: async () => (await api.get(`/cancel-reasons/${r.id}/email`)).data,
    enabled: editing,
  });
  useEffect(() => {
    if (mail && editing) setForm((f) => ({ ...f, email_subject: mail.subject || "", email_greeting: mail.greeting || "", email_body: mail.body || "" }));
  }, [mail, editing]);
  const save = useMutation({
    mutationFn: () => api.put(`/cancel-reasons/${r.id}`, form),
    onSuccess: () => { toast.success("Reason and its e-mail updated"); setEditing(false); qc.invalidateQueries({ queryKey: ["cancel-reason-email", r.id] }); refresh(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not update reason"),
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/cancel-reasons/${r.id}`),
    onSuccess: () => { toast.success(`"${r.label}" removed — restore it from "Removed reasons"`); onToggle(false); refresh(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not remove reason"),
  });
  if (editing) {
    return (
      <div className="py-2 px-2 space-y-2 rounded bg-white border border-slate-300" data-testid={`cancel-reason-edit-${r.id}`}>
        <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500">Reason</div>
        <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Reason label" className="h-8 text-sm" data-testid={`cancel-reason-edit-label-${r.id}`} />
        <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Short description (optional)" className="h-8 text-xs" data-testid={`cancel-reason-edit-desc-${r.id}`} />
        <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 pt-1">Customer e-mail for this reason {mailLoading && "· loading…"}</div>
        <Input value={form.email_subject} onChange={(e) => setForm({ ...form, email_subject: e.target.value })} placeholder="E-mail subject" className="h-8 text-sm" data-testid={`cancel-reason-edit-subject-${r.id}`} />
        <Input value={form.email_greeting} onChange={(e) => setForm({ ...form, email_greeting: e.target.value })} placeholder="Greeting, e.g. Hello {first_name}," className="h-8 text-xs" data-testid={`cancel-reason-edit-greeting-${r.id}`} />
        <Textarea value={form.email_body} onChange={(e) => setForm({ ...form, email_body: e.target.value })} rows={8} placeholder="E-mail body — leave an empty line between paragraphs" className="text-xs" data-testid={`cancel-reason-edit-body-${r.id}`} />
        <div className="text-[10px] text-slate-500">Tags: <span className="font-mono">{"{first_name}"}</span> <span className="font-mono">{"{order_number}"}</span> · the signature block (team, phone, e-mail) is added automatically.</div>
        <div className="flex justify-end gap-1">
          <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditing(false)}>Cancel</Button>
          <Button type="button" size="sm" className="h-7 text-xs bg-slate-900 text-white hover:bg-slate-800" disabled={save.isPending || mailLoading || !form.label.trim() || !form.email_body.trim()} onClick={() => save.mutate()} data-testid={`cancel-reason-save-${r.id}`}>Save reason + e-mail</Button>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2 py-1.5 hover:bg-white rounded px-1 group" data-testid={`cancel-reason-${r.id}`}>
      <input type="checkbox" className="mt-1 shrink-0" checked={checked} onChange={(e) => onToggle(e.target.checked)} data-testid={`cancel-reason-check-${r.id}`} />
      <label className="flex-1 min-w-0 cursor-pointer" onClick={() => onToggle(!checked)}>
        <div className="text-sm font-medium text-slate-800">{r.label}</div>
        {r.description && <div className="text-[11px] text-slate-500">{r.description}</div>}
      </label>
      <button type="button" title="Edit reason and its customer e-mail" className="text-slate-400 hover:text-slate-900 text-sm px-1" onClick={() => setEditing(true)} data-testid={`cancel-reason-edit-btn-${r.id}`}>✎</button>
      <button type="button" title="Remove reason from this list" className="text-slate-400 hover:text-rose-700 text-sm px-1" disabled={remove.isPending}
              onClick={() => { if (window.confirm(`Remove "${r.label}" from the cancel reasons? Past cancellations keep it; you can restore it later.`)) remove.mutate(); }}
              data-testid={`cancel-reason-remove-btn-${r.id}`}>✕</button>
    </div>
  );
}

// Collapsed list of removed reasons with a Restore button each.
export function RemovedCancelReasons({ enabled }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ["cancel-reasons", "all"],
    queryFn: async () => (await api.get("/cancel-reasons?include_inactive=1")).data,
    enabled,
  });
  const restore = useMutation({
    mutationFn: (id) => api.put(`/cancel-reasons/${id}`, { active: true }),
    onSuccess: () => { toast.success("Reason restored"); qc.invalidateQueries({ queryKey: ["cancel-reasons"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not restore reason"),
  });
  const removed = (data || []).filter((r) => r.active === false);
  if (!removed.length) return null;
  return (
    <div className="mt-2 border-t border-slate-200 pt-2" data-testid="cancel-removed-reasons">
      <button type="button" className="text-[11px] text-slate-500 hover:text-slate-800 underline" onClick={() => setOpen((v) => !v)} data-testid="cancel-removed-toggle">
        {open ? "Hide" : "Show"} removed reasons ({removed.length})
      </button>
      {open && removed.map((r) => (
        <div key={r.id} className="flex items-center gap-2 py-1 px-1 text-xs text-slate-500" data-testid={`cancel-removed-${r.id}`}>
          <span className="flex-1 line-through">{r.label}</span>
          <Button type="button" size="sm" variant="outline" className="h-6 text-[11px]" disabled={restore.isPending} onClick={() => restore.mutate(r.id)} data-testid={`cancel-restore-${r.id}`}>Restore</Button>
        </div>
      ))}
    </div>
  );
}
